/**
 * NDSS scoring engine.
 *
 *   category score = mean SPL of that category's questions x 25   (0-100)
 *   overall NDSS   = weighted mean of the eight category scores, rounded
 *
 * Category scores are kept UNROUNDED (Jurisdictional Resilience legitimately
 * lands on 87.5 when every answer is the best available, because Q4 caps at
 * SPL 3). Rounding happens at the display and persistence edge only.
 */

import { assertWeightsSumTo100, BANDS, CATEGORIES, QUESTIONS, splForAnswer } from './config.js';
import { splForScore } from './ndss-model.js';
import type { Band, CategoryResult, OptionIndex, ScanResult, Spl } from './types.js';

assertWeightsSumTo100();

export function bandFor(overall: number): Band {
  const band = BANDS.find((b) => overall >= b.min && overall <= b.max);
  if (!band) throw new Error(`No band defined for overall score ${overall}`);
  return band;
}

/**
 * Score from raw SPL values, one per question, in QUESTIONS order.
 *
 * This is the engine's real input. It is exported separately from
 * `scoreFromAnswers` so the scoring maths can be tested with SPL combinations
 * that no option set can produce (e.g. straight 2s across all twelve).
 */
export function scoreFromSpl(spls: readonly Spl[]): ScanResult {
  if (spls.length !== QUESTIONS.length) {
    throw new Error(`Expected ${QUESTIONS.length} SPL values, got ${spls.length}`);
  }

  const rawCategories = CATEGORIES.map((category) => {
    const memberSpls = QUESTIONS.reduce<number[]>((acc, question, i) => {
      if (question.categoryId === category.id) acc.push(spls[i]);
      return acc;
    }, []);

    if (memberSpls.length === 0) {
      throw new Error(`Category ${category.id} (${category.name}) has no questions`);
    }

    const meanSpl = memberSpls.reduce((a, b) => a + b, 0) / memberSpls.length;
    const score = meanSpl * 25;
    return {
      categoryId: category.id,
      code: category.code,
      name: category.name,
      shortTitle: category.shortTitle,
      weight: category.weight,
      score,
      mean: meanSpl,
      // How much of the full catalogue this category's score rests on.
      answered: memberSpls.length,
      catalogueTotal: category.catalogueTotal,
      spl: splForScore(score),
    };
  });

  const overall = Math.round(
    rawCategories.reduce((acc, c) => acc + c.score * c.weight, 0) / 100,
  );

  return {
    overall,
    spl: splForScore(overall),
    band: bandFor(overall),
    categories: tagCategories(rawCategories),
  };
}

/**
 * Score from stored answers (option indices 0-3), in QUESTIONS order.
 *
 * The index is NOT the SPL — it is mapped through the config table first.
 */
export function scoreFromAnswers(answers: readonly OptionIndex[]): ScanResult {
  if (answers.length !== QUESTIONS.length) {
    throw new Error(`Expected ${QUESTIONS.length} answers, got ${answers.length}`);
  }
  return scoreFromSpl(
    QUESTIONS.map((question, i) => splForAnswer(question.id, answers[i])),
  );
}

/**
 * Tag the two weakest categories "focus" and the strongest "strong".
 *
 * Ties break on category id so the output is deterministic. If every category
 * scores the same there is no meaningful weakest or strongest, so nothing is
 * tagged rather than tagging arbitrarily.
 */
function tagCategories(
  raw: readonly Omit<CategoryResult, 'tag'>[],
): CategoryResult[] {
  const allEqual = raw.every((c) => c.score === raw[0].score);
  if (allEqual) return raw.map((c) => ({ ...c, tag: null }));

  const byScore = [...raw].sort((a, b) => a.score - b.score || a.categoryId - b.categoryId);
  const focusIds = new Set(byScore.slice(0, 2).map((c) => c.categoryId));
  const strongest = byScore[byScore.length - 1];

  return raw.map((c) => {
    if (focusIds.has(c.categoryId)) return { ...c, tag: 'focus' as const };
    if (c.categoryId === strongest.categoryId) return { ...c, tag: 'strong' as const };
    return { ...c, tag: null };
  });
}
